import { all, call, fork, put, select, takeLatest, getContext, take } from 'redux-saga/effects';
import {
  ApplicantOfferStatus,
  ApplicationStatus,
  EApplicantOfferPGKeys,
  ErrorResponseBody,
  FeeStatus,
  IApplyCourse,
  IIntakeIdWithDates,
} from 'types';
import { getFiltersFailure, getFiltersSuccess } from '../actions';
import { offersDashboardActionTypes } from '../types';
import { logout } from '../../auth/actions';
import { getAuth, getDefaultIntakeId, getIntakeId, getIntakeRange } from '../../selectors';
import isTesting from '../../../utilities/isTesting';
import * as api from '../../../api/api';
import { AuthState } from '../../auth/types';
import { countries } from 'data';
import { getUniversityFromParams } from './helpers';
import { ConfigActionTypes } from '@/store/config/types';
import { getIsConfigLoaded, getTags } from '@/store/config/selectors';

import { isAgentOrAgencyRole } from '@/utilities/roles/permissions';
import { INTAKE_ALL_END_DATE, INTAKE_ALL_START_DATE } from '@/utilities/constants';

export interface IDropdownOptionMulti {
  label: string;
  value: string[];
}

export let getAllFiltersAbortController: AbortController;

export const createGetAllFilterParams = ({
  auth,
  intakeId,
  intakeRange,
  idToken,
}: {
  auth: AuthState;
  intakeId: string;
  intakeRange: IIntakeIdWithDates | undefined;
  idToken: string;
}) => {
  if (!intakeId && !isTesting) return;

  const params = new URLSearchParams(window.location.search);

  const isAgent = isAgentOrAgencyRole(auth.role);

  const startDate = isAgent && (intakeRange?.startDate ?? INTAKE_ALL_START_DATE);
  const endDate = isAgent && (intakeRange?.endDate ?? INTAKE_ALL_END_DATE);

  const uni = getUniversityFromParams(auth, params);

  const agentId = auth.agencyId;
  const officeId = auth?.currentUser?.agencyOffice;
  const currentUserRegionId = auth.currentUser?.regionAccessId;

  getAllFiltersAbortController = new AbortController();
  return {
    abortController: getAllFiltersAbortController,
    idToken,
    intakeId,
    startDate,
    endDate,
    uni,
    // office:
    agentId,
    officeId,
    currentUserRegionId,
  };
};

export function* handleGetAllFilters(): Generator {
  const params = new URLSearchParams(window.location.search);
  let res: { courses: IApplyCourse[] } | ErrorResponseBody;
  let courses: IApplyCourse[] = [];
  const auth: AuthState = yield select(getAuth);
  const intakeIdAuth: string = yield select(getIntakeId);
  const defaultIntake: string = yield select(getDefaultIntakeId);
  const intakeId = params.get('intakeId') || intakeIdAuth || defaultIntake;
  const intakeRange: IIntakeIdWithDates | undefined = yield select(getIntakeRange, intakeId);
  if (!intakeId && !isTesting) return;

  const isAgent = isAgentOrAgencyRole(auth.role);

  const context = yield getContext('sagaContext');
  const idToken: string = yield call(context.getIdToken);

  if (!intakeId && !isTesting) {
    return;
  }

  getAllFiltersAbortController = new AbortController();

  const getAllFiltersParams = createGetAllFilterParams({ auth, intakeId, intakeRange, idToken });

  try {
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.warn('Refresh Token has Expired - handleGetAllStudents');
      throw new Error('no Id token was provided, or idtoken was expired when calling getAllFilters saga.');
    }

    if (!getAllFiltersParams) {
      throw new Error('no params were resolved when calling getAllFilters saga.');
    }

    res = yield call(api.getAllApplyCourses, getAllFiltersParams as api.IGetAllParams);

    if ('error' in res) {
      throw new Error(JSON.stringify(res?.error) ?? 'An unknown error has occured');
    }

    courses = res?.courses ?? [];
  } catch (err) {
    console.warn('There was an error getting all filters');
    console.error(err);
    yield put(getFiltersFailure(err));
    return;
  }

  const availableNationalities = countries.map(country => ({
    label: country.nationality,
    value: country.nationality,
  }));

  const availableCountries = countries.map(country => ({
    label: country.countryName,
    value: country.countryName,
  }));

  const availableFeeStatus = [
    { label: 'Home', value: FeeStatus.Home },
    { label: 'Overseas', value: FeeStatus.Overseas },
    { label: 'Not sure', value: FeeStatus.NotSure },
  ];

  const availableAgentStatus = [
    { label: 'Not submitted', value: [ApplicationStatus.InProgress] },
    { label: 'Applied', value: [ApplicantOfferStatus.Applied] },
    { label: 'Conditional', value: [ApplicantOfferStatus.Conditional] },
    { label: 'Unconditional', value: [ApplicantOfferStatus.Unconditional] },
    { label: 'Rejected', value: [ApplicantOfferStatus.Rejected] },
    { label: 'Withdrawn', value: [ApplicantOfferStatus.Withdrawn] },
  ];

  const availableUniStatus = [
    { label: 'Not submitted', value: [ApplicationStatus.InProgress] },
    { label: 'Applied', value: [ApplicantOfferStatus.Applied] },
    { label: 'Conditional', value: [ApplicantOfferStatus.Conditional] },
    { label: 'Unconditional', value: [ApplicantOfferStatus.Unconditional] },
    { label: 'Rejected', value: [ApplicantOfferStatus.Rejected] },
    { label: 'Withdrawn', value: [ApplicantOfferStatus.Withdrawn] },
  ];

  const seenCourseNames = new Set<string>();
  const availableCourses =
    courses
      ?.filter(course => course.name && !seenCourseNames.has(course.name) && seenCourseNames.add(course.name))
      .map(course => ({ label: course.name, value: course.name })) ?? [];

  const tags = yield select(getTags);
  const availableTags =
    tags?.map(tag => ({
      label: tag.label,
      value: tag.id,
    })) ?? [];

  const availableFilters = {
    [EApplicantOfferPGKeys.NATIONALITY]: availableNationalities,
    [EApplicantOfferPGKeys.RESIDENCE_COUNTRY]: availableCountries,
    [EApplicantOfferPGKeys.APPLICATION_COUNTRY]: availableCountries,
    [EApplicantOfferPGKeys.BIRTH_COUNTRY]: availableCountries,

    [EApplicantOfferPGKeys.FEE_STATUS]: availableFeeStatus,
    [EApplicantOfferPGKeys.STATUS]: isAgent ? availableAgentStatus : availableUniStatus,

    [EApplicantOfferPGKeys.COURSE_NAME]: availableCourses,
    [EApplicantOfferPGKeys.TAGS]: availableTags,
  };

  yield put(getFiltersSuccess(availableFilters));
}

function* ensureConfigAndThenGetAllFilters(_action: any) {
  const isConfigLoaded: boolean = yield select(getIsConfigLoaded);
  if (!isConfigLoaded) {
    yield take([ConfigActionTypes.GET_UNI_CONFIG_SUCCESS]);
  }
  yield call(handleGetAllFilters);
}

function* watchGetFilters() {
  yield takeLatest(offersDashboardActionTypes.GET_FILTERS, ensureConfigAndThenGetAllFilters);
}

function* GetFiltersSaga() {
  yield all([fork(watchGetFilters)]);
}

export default GetFiltersSaga;
